import { defineMessages } from 'react-intl';

/**
 * Transifex-ready copy for the Product Roadmap page.
 * Each paragraph is a separate message so formatjs extract preserves them
 * and each is independently translatable. Bullet lists use `- item` syntax
 * rendered by `renderProductRoadmapRichText`. Inline `**bold**` is supported.
 */
export const productRoadmapMessages = defineMessages({
  seoTitle: {
    id: 'productRoadmap.seo.title',
    defaultMessage: "What's Next at Eventbrite | Product Roadmap",
    description: 'Document title for HeadSEO',
  },
  seoDescription: {
    id: 'productRoadmap.seo.description',
    defaultMessage:
      "{authorFullName} shares Eventbrite's roadmap: reliability, creator tools, discovery, ticketing, and checkout—with new capabilities shipping now.",
    description: 'Meta description for HeadSEO',
  },
  breadcrumbHome: {
    id: 'productRoadmap.breadcrumb.home',
    defaultMessage: 'Home',
    description: 'Breadcrumb label for site home',
  },
  breadcrumbProductUpdates: {
    id: 'productRoadmap.breadcrumb.productUpdates',
    defaultMessage: 'Product Updates',
    description: 'Breadcrumb label for Product Updates',
  },
  breadcrumbCurrent: {
    id: 'productRoadmap.breadcrumb.current',
    defaultMessage: 'Product Roadmap',
    description: 'Breadcrumb label for current page',
  },
  heroTitle: {
    id: 'productRoadmap.hero.title',
    defaultMessage: "What's next at Eventbrite",
    description: 'Hero H1 heading',
  },
  heroDek: {
    id: 'productRoadmap.hero.dek',
    defaultMessage:
      "{authorFullName}, Eventbrite's new lead, shares the team's roadmap for making Eventbrite the most powerful events platform for creators.",
    description: 'Hero dek',
  },
  introBylinePrefix: {
    id: 'productRoadmap.intro.bylinePrefix',
    defaultMessage: 'by',
    description: 'Lowercase word before author name in intro byline',
  },

  // ── Intro (4 paragraphs) ──────────────────────────────────────────────
  introPara1: {
    id: 'productRoadmap.intro.para1',
    defaultMessage:
      "Hello, I'm {authorFirstName}. I've recently stepped in to lead Eventbrite as part of the company's acquisition by Bending Spoons, which closed in early March.",
    description: 'Intro paragraph 1',
  },
  introPara2: {
    id: 'productRoadmap.intro.para2',
    defaultMessage:
      "Since then, we've been busy studying the business inside and out and investigating every corner of the product: parsing the data, examining the support queue, and speaking with hundreds of creators who use the platform every day.",
    description: 'Intro paragraph 2',
  },
  introPara3: {
    id: 'productRoadmap.intro.para3',
    defaultMessage:
      "We've done this to unlock Eventbrite's full potential as a **best-in-class events platform,** with the ticketing, event management, and discovery tools creators can rely on to fill the room again and again.",
    description: 'Intro paragraph 3',
  },
  introPara4: {
    id: 'productRoadmap.intro.para4',
    defaultMessage:
      "We're already at work on five core pillars of the Eventbrite platform: **reliability, creator tools, event reach and discovery, ticketing experience, and checkout**—with 12 new capabilities and improvements live as of the beginning of April, just a few weeks after the acquisition closed. That's the pace we're working with.",
    description: 'Intro paragraph 4',
  },

  // ── Section 0: What comes next? (3 paragraphs) ────────────────────────
  section0Title: {
    id: 'productRoadmap.section.0.title',
    defaultMessage: 'What comes next?',
    description: 'Roadmap section heading',
  },
  section0Para1: {
    id: 'productRoadmap.section.0.para1',
    defaultMessage:
      'Going forward, Bending Spoons team members are joining to accelerate product development, and our commitment to creator support remains as high as ever.',
    description: 'Section 0 paragraph 1',
  },
  section0Para2: {
    id: 'productRoadmap.section.0.para2',
    defaultMessage:
      "We're here for the long term. We've embedded some of our best engineers and designers fully into the product and have already begun shipping improvements—the first of many as we work to unlock Eventbrite's full potential.",
    description: 'Section 0 paragraph 2',
  },
  section0Para3: {
    id: 'productRoadmap.section.0.para3',
    defaultMessage: "We've outlined our top priorities, and progress to date, below.",
    description: 'Section 0 paragraph 3',
  },

  // ── Section 1: Accelerated product development (2 paragraphs) ─────────
  section1Title: {
    id: 'productRoadmap.section.1.title',
    defaultMessage: 'Accelerated product development',
    description: 'Roadmap section heading',
  },
  section1Para1: {
    id: 'productRoadmap.section.1.para1',
    defaultMessage:
      "**We're here to make Eventbrite the most reliable and powerful events platform for the creators and attendees who depend on it.** We'll build new features and revisit existing tools to make them more powerful and intuitive to use.",
    description: 'Section 1 paragraph 1',
  },
  section1Para2: {
    id: 'productRoadmap.section.1.para2',
    defaultMessage:
      'To guide our focus, we spoke with hundreds of creators, reviewed thousands of support tickets, and personally tested every tool and workflow available to creators. From there, we shaped our plan for the coming months. It revolves around five pillars:',
    description: 'Section 1 paragraph 2',
  },

  // ── Section 2: Platform reliability (paragraph + bullets) ─────────────
  section2Title: {
    id: 'productRoadmap.section.2.title',
    defaultMessage: 'Platform reliability',
    description: 'Roadmap section heading',
  },
  section2Para1: {
    id: 'productRoadmap.section.2.para1',
    defaultMessage:
      "Events are intense by nature, and the tools you rely on should always hold up when it counts. We're investing heavily in Eventbrite's reliability to ensure it's faster during key workflows and more stable during the most crucial moments on event day. We're also fixing the login issues that create stress for organizers and attendees who may find themselves locked out right as their event starts.",
    description: 'Section 2 paragraph 1',
  },
  alreadyLive: {
    id: 'productRoadmap.alreadyLive',
    defaultMessage: 'Already live this month:',
    description: 'Shared label before bullet lists of shipped features',
  },
  section2Bullets: {
    id: 'productRoadmap.section.2.bullets',
    defaultMessage:
      '- Faster event creation: Creating an event is faster end-to-end: load times are down by -40%, and the interface stays responsive as you move through it. Loading times between each step will soon be significantly reduced too.',
    description: 'Section 2 bullet list',
  },

  // ── Section 3: Creator tools (paragraph + bullets, split for visuals) ─
  section3Title: {
    id: 'productRoadmap.section.3.title',
    defaultMessage: 'Creator tools',
    description: 'Roadmap section heading',
  },
  section3Para1: {
    id: 'productRoadmap.section.3.para1',
    defaultMessage:
      "Managing an event requires a thousand small tasks and decisions. Eventbrite is becoming the tool that makes each one of them easier. This means a more intuitive and efficient platform, from check-in and reporting to refunds and payouts. We're also exploring the capabilities that matter most to creators running events at scale, such as direct SMS communication, reserved seating configurations, bulk attendee transfers, and more powerful waitlist management.",
    description: 'Section 3 paragraph 1',
  },
  section3Bullets: {
    id: 'productRoadmap.section.3.bullets',
    defaultMessage:
      "- Question ordering at checkout: Whether you're selling tickets or running a free event, a complete registration often means capturing the right information at the right moment. Now you decide where your custom questions appear in the process—before or after the attendee confirms their spot.\n- Save before publishing: You could already save parts of your event as a draft, but we've now extended this capability to all steps in the event creation flow.\n- Check-in window: Set a specific time window for scanning tickets at the door. Useful for enforcing early access vs. general admission, or preventing entry before a set time.\n- Multi-slot scanning: For events with multiple overlapping time slots, operators can now scan attendees across all relevant slots at once from the organizer app, with no switching between sessions at the door.",
    description: 'Creator tools — all bullets',
  },

  // ── Section 4: Event reach and discovery (paragraph + bullets, split) ──
  section4Title: {
    id: 'productRoadmap.section.4.title',
    defaultMessage: 'Event reach and discovery',
    description: 'Roadmap section heading',
  },
  section4Para1: {
    id: 'productRoadmap.section.4.para1',
    defaultMessage:
      "Every creator on Eventbrite is working toward the same objective: a bigger audience, and one that keeps coming back. Beyond upgrading the reach and discovery tools already in place, we'll be working on the improvements that make that possible: marketing tools to promote your events on and off Eventbrite, attribution and conversion reporting, and better SEO for event listings so more people find you organically.",
    description: 'Section 4 paragraph 1',
  },
  section4Bullet1: {
    id: 'productRoadmap.section.4.bullet1',
    defaultMessage:
      "- Creator profile page: A complete overhaul of your public profile—your storefront on Eventbrite. Now includes: a cover image, a media gallery where you can include pictures from past events, key stats, and links to your website and social channels. We're also working on a calendar of upcoming events.",
    description: 'Event reach — creator profile bullet',
  },
  section4Bullet2: {
    id: 'productRoadmap.section.4.bullet2',
    defaultMessage:
      '- Top Organizer badges: The creators who consistently show up and deliver deserve to be recognized. They will earn a badge visible across their profile and event listings: a signal of quality and trust for new attendees discovering the platform.',
    description: 'Event reach — Top Organizer badges bullet',
  },
  section4Bullet3: {
    id: 'productRoadmap.section.4.bullet3',
    defaultMessage:
      '- Event flyer tool: Generate a printable flyer with your event image, key details, and a QR code linking to your ticket page in multiple formats to be posted on venue windows, local boards, or anywhere you want to drive walk-up sales.',
    description: 'Event reach — Event flyer bullet',
  },
  section4Bullet4: {
    id: 'productRoadmap.section.4.bullet4',
    defaultMessage:
      '- Higher-resolution event images: Your event listing photos now display at double the resolution. Your events look sharper everywhere they appear.',
    description: 'Event reach — higher-resolution bullet',
  },

  // ── Section 5: A modern ticketing experience (paragraph + bullets) ────
  section5Title: {
    id: 'productRoadmap.section.5.title',
    defaultMessage: 'A modern ticketing experience',
    description: 'Roadmap section heading',
  },
  section5Para1: {
    id: 'productRoadmap.section.5.para1',
    defaultMessage:
      "Attendees should always be able to find their ticket—regardless of how they signed up, or whether they have signal at the door. A smooth, reliable ticketing experience reduces stress for organizers and attendees alike. We're also working on automatic event update notifications: Registered attendees will be notified by email whenever you change the date, time, location, or key details of an event, with no manual message needed.",
    description: 'Section 5 paragraph 1',
  },
  section5Bullet1: {
    id: 'productRoadmap.section.5.bullet1',
    defaultMessage:
      "- Save to Apple Wallet / Google Wallet: From the mobile website, attendees can save their ticket to their phone's wallet in one tap.",
    description: 'Ticketing — Wallet bullet',
  },
  section5Bullet2: {
    id: 'productRoadmap.section.5.bullet2',
    defaultMessage:
      '- Tickets available offline: Through the Eventbrite app, tickets are accessible without a connection and ready at the door.',
    description: 'Ticketing — offline tickets bullet',
  },

  // ── Section 6: Checkout (paragraph + bullets) ─────────────────────────
  section6Title: {
    id: 'productRoadmap.section.6.title',
    defaultMessage: 'Checkout',
    description: 'Roadmap section heading',
  },
  section6Para1: {
    id: 'productRoadmap.section.6.para1',
    defaultMessage:
      "Checkout is the most delicate and important part of the process. We're focused on making every step as simple and fast as possible to turn people's interest into action. We're also working on making add-ons more visible at checkout, so you can sell more in addition to your tickets.",
    description: 'Section 6 paragraph 1',
  },
  section6Bullets: {
    id: 'productRoadmap.section.6.bullets',
    defaultMessage:
      '- Clearer confirmation emails: We polished various elements of the confirmation emails, such as the clarity of calls to action and the inclusion of relevant information like the answers to custom questions.',
    description: 'Section 6 bullet list',
  },

  // ── Closing (separate paragraphs for proper rendering) ────────────────
  closingPara1: {
    id: 'productRoadmap.closing.para1',
    defaultMessage:
      'And this is just April. We have passionate teams working on this every day, and we have no plans to slow down. You can follow our monthly progress in the new <productUpdates>dedicated product updates page</productUpdates> or get a quarterly update in your inbox by <subscribeHere>subscribing here</subscribeHere>.',
    description: 'Closing paragraph 1 — product updates + subscribe links.',
  },
  closingPara2: {
    id: 'productRoadmap.closing.para2',
    defaultMessage:
      "The team and I are excited to take up Eventbrite's mission to help you build communities, and create unforgettable live experiences. I look forward to connecting more with many of you in the coming months.",
    description: 'Closing paragraph 2 — plain text.',
  },
  closingPara3: {
    id: 'productRoadmap.closing.para3',
    defaultMessage:
      'For any support-related inquiries, you can contact the Eventbrite support team here: <getSupport>support link</getSupport>.',
    description: 'Closing paragraph 3 — support link.',
  },
  closingPara4: {
    id: 'productRoadmap.closing.para4',
    defaultMessage:
      'If you have thoughts on what we should tackle next, we want to hear them: <shareFeedback>request an improvement</shareFeedback>. Your input will directly shape what we prioritize next.',
    description: 'Closing paragraph 4 — feedback link.',
  },
  closingSignoff: {
    id: 'productRoadmap.closing.signoff',
    defaultMessage: 'Until next time 👋',
    description: 'Closing sign-off line.',
  },
});

/** Keyed map of section message descriptors (title + first body paragraph) in display order. */
export const productRoadmapSections = {
  whatComesNext: {
    title: productRoadmapMessages.section0Title,
  },
  acceleratedDev: {
    title: productRoadmapMessages.section1Title,
  },
  platformReliability: {
    title: productRoadmapMessages.section2Title,
  },
  creatorTools: {
    title: productRoadmapMessages.section3Title,
  },
  eventReach: {
    title: productRoadmapMessages.section4Title,
  },
  ticketing: {
    title: productRoadmapMessages.section5Title,
  },
  checkout: {
    title: productRoadmapMessages.section6Title,
  },
  closing: {},
} as const;

export type ProductRoadmapSectionKey = keyof typeof productRoadmapSections;
